import { ReactElement, useEffect, useState } from "react";
import { IonContent, IonIcon, IonPage, IonSpinner, IonToggle } from "@ionic/react";
import {
  chatbubbleEllipsesOutline,
  checkmarkCircleOutline,
  mailOutline,
  notificationsOutline,
} from "ionicons/icons";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import useQuery from "../../hooks/useQuery";
import {
  getPublicNotificationPreferences,
  patchPublicNotificationPreferences,
  PublicNotificationPreferences,
} from "../../services/user.service";

type Channel = "email" | "sms" | "push";

const CHANNELS: { key: Channel; labelKey: string; icon: string }[] = [
  { key: "email", labelKey: "mentalplus_account_notification_email", icon: mailOutline },
  { key: "sms", labelKey: "mentalplus_account_notification_sms", icon: chatbubbleEllipsesOutline },
  { key: "push", labelKey: "mentalplus_account_notification_push", icon: notificationsOutline },
];

const UnsubscribePage = (): ReactElement => {
  const { t, i18n } = useTranslation();
  const query = useQuery();
  const token = query.get("token") ?? "";

  const [prefs, setPrefs] = useState<PublicNotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [saving, setSaving] = useState<Channel | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!token) {
        setInvalid(true);
        setLoading(false);
        return;
      }
      try {
        const { data } = await getPublicNotificationPreferences(token);
        if (!active) return;
        setPrefs(data);
        // Render the page in the participant's study language when available.
        const lang = data.study?.lang?.toLowerCase();
        if (lang) i18n.changeLanguage(lang);
      } catch (_error) {
        if (active) setInvalid(true);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [token]);

  const onToggle = async (channel: Channel, value: boolean) => {
    if (!prefs || saving) return;
    const previous = prefs;
    setPrefs({ ...prefs, [channel]: value });
    setSaving(channel);
    setSaved(false);
    setError(false);
    try {
      const { data } = await patchPublicNotificationPreferences(token, { [channel]: value });
      setPrefs(data);
      setSaved(true);
    } catch (_error) {
      setPrefs(previous); // revert optimistic update
      setError(true);
    } finally {
      setSaving(null);
    }
  };

  return (
    <IonPage>
      <IonContent>
        <Wrap>
          <Card>
            {loading ? (
              <Centered>
                <IonSpinner name="crescent" />
                <Muted>{t("mentalplus_unsubscribe_loading")}</Muted>
              </Centered>
            ) : invalid || !prefs ? (
              <Centered>
                <Title>{t("mentalplus_unsubscribe_title")}</Title>
                <Muted>{t("mentalplus_unsubscribe_invalid")}</Muted>
              </Centered>
            ) : (
              <>
                <Title>{t("mentalplus_unsubscribe_title")}</Title>
                {prefs.study?.name ? <StudyName>{prefs.study.name}</StudyName> : null}
                <Intro>{t("mentalplus_unsubscribe_intro")}</Intro>

                {CHANNELS.map(({ key, labelKey, icon }) => (
                  <Row key={key}>
                    <RowLabel>
                      <IonIcon icon={icon} />
                      <span>{t(labelKey)}</span>
                    </RowLabel>
                    <StyledToggle
                      checked={prefs[key]}
                      disabled={saving === key}
                      onIonChange={(event) => onToggle(key, event.detail.checked)}
                    />
                  </Row>
                ))}

                <PushNote>{t("mentalplus_unsubscribe_push_note")}</PushNote>

                {saved ? (
                  <SavedNote>
                    <IonIcon icon={checkmarkCircleOutline} />
                    <span>{t("mentalplus_unsubscribe_saved")}</span>
                  </SavedNote>
                ) : null}
                {error ? <ErrorNote>{t("mentalplus_unsubscribe_error")}</ErrorNote> : null}
              </>
            )}
          </Card>
        </Wrap>
      </IonContent>
    </IonPage>
  );
};

export default UnsubscribePage;

const Wrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 32px 16px;
  min-height: 100%;
  background-color: var(--app-color-background, #f2f3f8);
`;

const Card = styled.div`
  width: 100%;
  max-width: 480px;
  background: var(--app-color-surface-tint, #fff);
  border-radius: 12px;
  box-shadow: 0 6px 18px 0 rgba(0, 0, 0, 0.06);
  padding: 28px 24px;
`;

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 4px;
  color: var(--app-color-text, #2c3e50);
`;

const StudyName = styled.p`
  font-size: 14px;
  font-weight: 600;
  /* Emphasis = strong tone derived from primary (tokens.ts) — primary itself
     may be a pastel chrome pick, too light for text on white. */
  color: var(--app-color-emphasis, var(--app-color-primary, #2d9f75));
  margin: 0 0 12px;
`;

const Intro = styled.p`
  font-size: 14px;
  color: rgba(69, 80, 86, 0.8);
  margin: 0 0 20px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 0;
  border-top: 1px solid #eef1f5;
`;

const RowLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  color: var(--app-color-text, #2c3e50);

  ion-icon {
    font-size: 20px;
    color: rgba(69, 80, 86, 0.7);
  }
`;

const StyledToggle = styled(IonToggle)`
  --handle-width: 18px;
  --handle-height: 18px;
  --track-background: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.25);
  /* Toggle = interactive control -> accent family (see tokens.ts roles). */
  --track-background-checked: var(--app-color-accent, #2d9f75);
`;

const Muted = styled.p`
  font-size: 14px;
  color: rgba(69, 80, 86, 0.7);
  margin: 0;
`;

const PushNote = styled.p`
  font-size: 12px;
  color: rgba(69, 80, 86, 0.6);
  margin: 14px 0 0;
`;

const SavedNote = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  font-size: 14px;
  color: #2d9f75;

  ion-icon {
    font-size: 18px;
  }
`;

const ErrorNote = styled.p`
  margin-top: 16px;
  font-size: 14px;
  color: #d9534f;
`;
